/**
 * Lógica pura (sin SolidJS ni crypto) del vault, aislada para poder testearla:
 *   - máquina de estado bloqueo/desbloqueo,
 *   - derivación de la lista de items,
 *   - mapeo del informe de salud a contadores de UI.
 */

import type { VaultStatus } from "./vault-context";

export interface VaultStatusInputs {
  /** La BD local (PowerSync) está lista. */
  readonly dbReady: boolean;
  /** La query de la cuenta aún está cargando. */
  readonly accountLoading: boolean;
  /** Existe una fila `vault_account` local (setup hecho). */
  readonly hasAccount: boolean;
  /** Tenemos las claves en memoria (desbloqueado). */
  readonly hasKeys: boolean;
}

/** Deriva el estado del vault de forma determinista (misma lógica que el contexto). */
export function deriveVaultStatus(inputs: VaultStatusInputs): VaultStatus {
  if (!inputs.dbReady || inputs.accountLoading) {
    return "loading";
  }
  if (!inputs.hasAccount) {
    return "needs-setup";
  }
  return inputs.hasKeys ? "unlocked" : "locked";
}

export type VaultItemType = "login" | "note" | "card" | "identity";

export interface ListableItem {
  readonly id: string;
  readonly type: VaultItemType;
  readonly name: string;
  readonly username?: string;
  readonly updatedAt?: string;
}

/**
 * Ordena y filtra los items descifrados para la lista: por búsqueda (nombre o
 * usuario, case-insensitive) y luego alfabéticamente por nombre.
 */
export function deriveItemList(items: ListableItem[], query = ""): ListableItem[] {
  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter(
        (i) => i.name.toLowerCase().includes(q) || (i.username ?? "").toLowerCase().includes(q),
      )
    : items;
  return filtered.toSorted((a, b) => a.name.localeCompare(b.name));
}

/**
 * Formatea un código TOTP para leerlo mejor: un código de 6 dígitos se parte en
 * dos grupos de 3 ("123456" → "123 456"). Cualquier otra longitud se devuelve
 * tal cual (no asumimos formato).
 */
export function formatTotpCode(code: string): string {
  return code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code;
}

/**
 * Estado real de un item para la vista de inventario. Deriva del análisis de
 * salud (débil/reutilizada/antigua) — NO de un icono estático. Prioridad
 * descendente: `weak` > `reused` > `stale` > `ok`.
 */
export type ItemStatus = "weak" | "reused" | "stale" | "ok";

export interface InventoryReport {
  readonly weak: readonly string[];
  readonly reused: readonly (readonly string[])[];
  readonly old: readonly string[];
}

export interface InventoryRow extends ListableItem {
  /** Estado derivado del análisis real (no un icono fijo por tipo). */
  readonly status: ItemStatus;
}

/**
 * Deriva las filas de inventario del ESTADO REAL: combina la lista de items
 * (tipo, nombre, última actividad) con el informe de salud vivo para etiquetar
 * cada item con su estado actual, en lugar de un icono fijo desactualizado.
 * Los items sin contraseña (nota/identidad) quedan `ok`.
 */
export function deriveInventory(
  items: ListableItem[],
  report: InventoryReport,
  query = "",
): InventoryRow[] {
  const weak = new Set(report.weak);
  const reused = new Set(report.reused.flat());
  const stale = new Set(report.old);
  return deriveItemList(items, query).map((item) => ({
    ...item,
    status: statusFor(item.id, weak, reused, stale),
  }));
}

function statusFor(
  id: string,
  weak: Set<string>,
  reused: Set<string>,
  stale: Set<string>,
): ItemStatus {
  if (weak.has(id)) {
    return "weak";
  }
  if (reused.has(id)) {
    return "reused";
  }
  if (stale.has(id)) {
    return "stale";
  }
  return "ok";
}

export interface HealthSummary {
  readonly total: number;
  readonly weak: number;
  readonly reused: number;
  readonly healthy: number;
}

export interface RawHealthReport {
  readonly weak: readonly string[];
  readonly reused: readonly (readonly string[])[];
}

/**
 * Mapea el informe crudo de `crypto.analyzeVaultHealth` a contadores de UI.
 * `total` es el número de items con contraseña analizados. `healthy` = los que
 * no son ni débiles ni reutilizados.
 */
export function summarizeHealth(report: RawHealthReport, total: number): HealthSummary {
  const weakSet = new Set(report.weak);
  const reusedSet = new Set(report.reused.flat());
  const unhealthy = new Set<string>([...weakSet, ...reusedSet]);
  return {
    total,
    weak: weakSet.size,
    reused: reusedSet.size,
    healthy: Math.max(0, total - unhealthy.size),
  };
}
